import { useContext, useEffect, useState } from "react";
import { CircularProgress, Paper, Typography, Stack} from "@mui/material";
import { useHistory, useParams } from "react-router";
import { Box } from "@mui/system";
import { Helmet } from "react-helmet";

import moment from "moment";

import useFields, { FILTER_TYPES } from "../hooks/useFields";
import useObject from "../hooks/useObject";

import EditEntity from "../components/EditEntity";

import EntityOverview from "../views/EntityOverview";
import CreateItemButton from "../components/CreateItemButton";
import MFilesButton from "../components/MFilesButton";

import { exportToFile } from "../utils/utils";

import useUserPermissionsContext from "../hooks/useUserPermissionsContext";
import UserRights from "../enums/UserRights";
import { exportRecords } from "../dao/common";
import { errorMessageHandler } from "../utils/error";
import ButtonWithSpinner from "../components/ButtonWithSpinner";
import EditModes from "../enums/EditModes";
import { ROUTES } from "../enums/Routes";
import { GlobalContext } from "../globalContext";
import { formatPageTitle } from "../utils/pageTitle";
import { API_ROUTES } from "../enums/api";
import { create, softDelete, update } from "../dao/operations";

const EXPORT_DATE_FORMAT = "YYYYMMDD";
const API_ROUTE = API_ROUTES["objects"];

export default function ObjectDetail({ title = "", mode = "" }) {
  const [isExportingObject, setIsExportingObject] = useState(false);
  const [isExportingEquipment, setIsExportingEquipment] = useState(false);
  const [isExportingCables, setIsExportingCables] = useState(false);
  const history = useHistory();
  const { objectId } = useParams();

  const isUpdateMode = mode === EditModes.UPDATE;
  const isCreateMode = mode === EditModes.CREATE;

  const { isAllowedTo, isLoading } = useUserPermissionsContext();
  const { setObjectCreatedCount, setGlobalObjectFilter } = useContext(GlobalContext);

  const isAllowedToUpdate = isAllowedTo(UserRights.UPDATE, FILTER_TYPES.object);
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.object
  );
  const isAllowedToCreate = isAllowedTo(UserRights.CREATE, FILTER_TYPES.object);

  const { object, fetchObjectData } = useObject(objectId);

  const pageTitle = formatPageTitle(title);

  const [fields] = useFields(
    FILTER_TYPES.object,
    false,
    isCreateMode,
    isUpdateMode
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const storeObject = async (payload) => {
    if (isUpdateMode) {
      await update({ ...payload, id: object.id }, { route: API_ROUTE }).then(
        () => {
          fetchObjectData();
        }
      );
    } else {
      await create({ ...payload }, { route: API_ROUTE }).then((res) => {
        setObjectCreatedCount((prev) => prev + 1);
        setGlobalObjectFilter({
          columnField: "object_id",
          operatorValue: "contains",
            value: res.id,
          init: false,
        });
        history.push(`/object/${res.id}`);
      });
    }
  };

  const removeObject = async (payload) => {
    if (isUpdateMode) {
      return softDelete({ route: API_ROUTE, delete: true })(payload).then(
        () => {
          fetchObjectData();
        }
      );
    }
  };

  const cancelChanges = async () => {
    if (isUpdateMode) {
      fetchObjectData();
    } else {
      history.goBack();
    }
  };

  const restoreDeletedObject = async (payload) => {
    return softDelete({ route: API_ROUTE, delete: false })(payload).then(() => {
      fetchObjectData();
    });
  };

  const handleExportObjectClick = () => {
    setIsExportingObject(true);
    const abortController = new AbortController();
    exportRecords(`/eplan/objects/${object.id}`, {}, abortController)
      .then((response) => {
        const currentDate = moment().format(EXPORT_DATE_FORMAT);
        exportToFile(`${object.number}_EPLAN_OBJ_${currentDate}.csv`, response);
      })
      .catch((err) => {
        errorMessageHandler(err);
      })
      .finally(() => {
        setIsExportingObject(false);
      });
  };

  const handleExportEquipmentClick = () => {
    setIsExportingEquipment(true);
    const abortController = new AbortController();
    const type = "application/xlsx";
    exportRecords(
      `/eplan/objects/${object.id}/equipments`,
      {},
      abortController,
      type
    )
      .then((response) => {
        const currentDate = moment().format(EXPORT_DATE_FORMAT);
        exportToFile(
          `${object.number}_EPLAN_EQM_${currentDate}.xlsx`,
          response,
          type
        );
      })
      .catch((err) => {
        errorMessageHandler(err);
      })
      .finally(() => {
        setIsExportingEquipment(false);
      });
  };

  const handleExportCablesClick = () => {
    setIsExportingCables(true);
    const abortController = new AbortController();
    const type = "application/xlsx";
    exportRecords(
      `/eplan/objects/${object.id}/cables`,
      {},
      abortController,
      type
    )
      .then((response) => {
        const currentDate = moment().format(EXPORT_DATE_FORMAT);
        exportToFile(
          `${object.number}_EPLAN_CAB_${currentDate}.xlsx`,
          response,
          type
        );
      })
      .catch((err) => {
        errorMessageHandler(err);
      })
      .finally(() => {
        setIsExportingCables(false);
      });
  };

  return !fields || !object ? (
    <Box
      sx={{
        marginTop: 3,
        textAlign: "center",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Paper sx={{ padding: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <Typography variant='h5'>{title}</Typography>
          {isUpdateMode && (
            <Stack direction={"row"} spacing={1}>
              <MFilesButton hyperlink={object.m_files_hyperlink} />
              <CreateItemButton
                entity={FILTER_TYPES.object}
                size='medium'
                to={ROUTES.object.create}
                isDisabled={isAllowedToCreate === false}
              />
            </Stack>
          )}
          </Box>

        {isLoading === false && (
          <>
            <EditEntity
              isAllowedToUpdate={isAllowedToUpdate}
              isAllowedToDelete={isAllowedToDelete}
              isAllowedToCreate={isAllowedToCreate}
              entity={object}
              fields={fields}
              storeEntity={storeObject}
              deleteEntity={removeObject}
              entityType={FILTER_TYPES.object}
              cancel={cancelChanges}
              restoreEntity={restoreDeletedObject}
              isUpdateMode={isUpdateMode}
            />
            {isUpdateMode && (
              <>
                <ButtonWithSpinner
                  onClick={handleExportObjectClick}
                  variant='text'
                  color='primary'
                  title={"Export"}
                  loading={isExportingObject}
                  disabled={isExportingObject}
                >
                  Export
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  onClick={handleExportEquipmentClick}
                  variant='text'
                  color='primary'
                  title={"Export Equipment/Sections"}
                  loading={isExportingEquipment}
                  disabled={isExportingEquipment}
                >
                  Export Equipment/Sections
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  onClick={handleExportCablesClick}
                  variant='text'
                  color='primary'
                  title={"Export Cables"}
                  loading={isExportingCables}
                  disabled={isExportingCables}
                >
                  Export Cables
                </ButtonWithSpinner>
              </>
            )}
          </>
        )}
        {isUpdateMode && (
          <>
            <EntityOverview
              entity={FILTER_TYPES.project}
              title={"Projects overview"}
              nestedEntity={true}
              parent={{
                object_id: object.id,
              }}
            />
            <EntityOverview
              entity={FILTER_TYPES.system_group}
              title={"System groups overview"}
              nestedEntity={true}
              parent={{
                object_id: object.id,
              }}
            />
            <EntityOverview
              entity={FILTER_TYPES.location}
              title={"Locations overview"}
              nestedEntity={true}
              parent={{
                object_id: object.id,
              }}
            />
            <EntityOverview
              entity={FILTER_TYPES.equipment}
              title={"Equipments overview"}
              nestedEntity={true}
              parent={{
                object_id: object.id,
              }}
            />
            <EntityOverview
              entity={FILTER_TYPES.cable_bundle}
              title={"Bundles overview"}
              nestedEntity={true}
              parent={{
                object_id: object.id,
              }}
            />
          </>
        )}
      </Paper>
    </>
  );
}
